/**
 * Outbound webhooks: the public event catalogue and the retry schedule.
 *
 * Events are derived from the audit log (every mutation already writes one in
 * its own transaction), so the catalogue is a mapping from audit actions to
 * public event names. Each event names the permission an endpoint's creator
 * must hold to subscribe — a webhook is a standing read, and it must not be a
 * way around `giving:read` or a child's check-in record.
 */

export const WEBHOOK_EVENTS = {
  'person.created': {
    label: 'A person is added',
    actions: ['person.created'],
    permission: 'people:read',
  },
  'person.updated': {
    label: 'A person’s details change',
    actions: [
      'person.updated',
      'person.self_updated',
      'person.contacts_updated',
      'person.custom_fields_updated',
    ],
    permission: 'people:read',
  },
  'person.deleted': {
    label: 'A person is removed or merged',
    actions: ['person.deleted', 'person.merged'],
    permission: 'people:read',
  },
  'group.member_added': {
    label: 'Someone joins a group',
    actions: ['group.member_added'],
    permission: 'groups:read',
  },
  'attendance.recorded': {
    label: 'Attendance is taken',
    actions: ['attendance.recorded'],
    permission: 'attendance:read',
  },
  'event.created': {
    label: 'An event is created',
    actions: ['event.created'],
    permission: 'events:read',
  },
  'registration.created': {
    label: 'Someone registers',
    actions: ['registration.created'],
    permission: 'events:read',
  },
  'registration.cancelled': {
    label: 'A registration is cancelled',
    actions: ['registration.cancelled'],
    permission: 'events:read',
  },
  'donation.created': {
    label: 'A gift is recorded',
    actions: ['donation.recorded'],
    permission: 'giving:read',
  },
  'checkin.created': {
    label: 'A child is checked in',
    actions: ['checkin.created'],
    permission: 'checkin:admin',
  },
} as const

export type WebhookEventType = keyof typeof WEBHOOK_EVENTS
export const WEBHOOK_EVENT_TYPES = Object.keys(WEBHOOK_EVENTS) as WebhookEventType[]

/** Audit action → public event type. */
export function eventTypeForAction(action: string): WebhookEventType | null {
  for (const type of WEBHOOK_EVENT_TYPES)
    if ((WEBHOOK_EVENTS[type].actions as readonly string[]).includes(action)) return type
  return null
}

/** Attempts before a delivery is marked failed: about a day of trying. */
export const WEBHOOK_MAX_ATTEMPTS = 8

/**
 * Exponential backoff: 1, 2, 4, 8 … minutes after attempt N, capped at six
 * hours, with ±20% jitter so a receiver that was down for everyone is not
 * hit by every church in the same second when it comes back.
 * `random` is injectable so the schedule is testable.
 */
export function nextAttemptDelayMs(attempt: number, random: () => number = Math.random): number {
  const base = Math.min(60_000 * 2 ** Math.max(0, attempt - 1), 6 * 3_600_000)
  return Math.round(base * (0.8 + random() * 0.4))
}
